import React, {useState, useEffect, useContext} from 'react';
import ReactDOM from 'react-dom/client';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';

import { ResultsPageControl, ResultsView, RESULTS_MODE } from '../results.js';
import { useElasticSearch, searchContextToQuery, paramsToState, stateToParams } from '../util.js';

import { esQueries } from '../../../js/elastic.js';
import { FilterBar } from '../components/filterBar.js';
import { SearchConfigContext, SearchConfigProvider } from '../context/searchConfigContext.js';
import { dataMapping } from '../context/dataMapping.js';
import { searchPageSearchConfig } from '../context/searchConfig.js';

import '../../css/search.scoped.scss';

const BackNavButton = (props) => {
  return <div className={`backnav-button is-block is-one-half-mobile is-three-fifths-tablet ${props.hidden ? 'is-hidden' : '' }`}>
            <div>
              <a href={props.link} id="section-archive-series-details-back">&lt; {props.label}</a>
            </div>
          </div>

}

const YouSearched = (props) => {
  let titleNode;
  if (props.searchStr === '') {
    titleNode = <React.Fragment>{props.typeLabel}</React.Fragment>
  } else {
    titleNode = <React.Fragment>{props.resultsForLabel}&nbsp;
                  <span id="section-search-search-text-label">“{props.searchStr}”</span>
                </React.Fragment>
  }

  return <div className="page-headline" role="heading" aria-level="1">{titleNode}</div>
}

const SearchTab = (props) => {

  let label = props.type === "subquery" ? `${props.label} (${props.tabCount})` : props.label

  const isLink = (props.type === 'link')
  const optionalHref = isLink ? { href: props.tabConfig.href, target: '_blank' } : {}
  return <li className={ `${ props.activeTab === props.name ? "is-active active-tab" : '' } has-text-weight-semibold` }>
            <a {...optionalHref} role={ props.type !== 'link' ? "heading" : "link" } aria-level="2" data-target="section-archive-collections" onClick={ (event) => { if (isLink) return ; props.callback(props.name); event.preventDefault(); } }>
              <span>{label}</span>
            </a>
          </li>

}

const SearchTabBar = (props) => {
  let countableTabs = props.tabConfigs.filter( cfg => cfg.type === "subquery" ).map( cfg => cfg.name )

  return <div className="tabs is-boxed">
          <ul>
            { props.tabConfigs.map( (tabConfig,idx) => <SearchTab {...props} 
                                                                  key={idx} 
                                                                  tabCount={ countableTabs.find( item => item === tabConfig.name) && props.tabCounts?.responses?.length > countableTabs.indexOf(tabConfig.name) ? props.tabCounts?.responses[countableTabs.indexOf(tabConfig.name)].hits?.total : '' } 
                                                                  type={tabConfig.type} 
                                                                  name={tabConfig.name} 
                                                                  label={tabConfig.label}
                                                                  tabConfig={tabConfig} 
                                                                  callback={props.clickCallback} />
              ) 
            }
          </ul>
        </div>

}

const SearchView = (props) => {

    const config = useContext(SearchConfigContext)
    const endpoint = config.endpoint;
    const mapping = config.mapping;
    const labelConfig = config.searchConfig.labels.reduce((acc,cur) => { acc[cur.name] = cur.label ; return acc }, {} )
    const defaultSortConfig = config.searchConfig.sort.filter( item => item.default ).reduce( (acc,cur) => { return { ...cur } }, {})

    // const receivedFilters = config.filters;
    const searchConfig = config.searchConfig;

    const tabConfig = config.searchConfig.tabs?.reduce((acc,cur) => { acc.push({ ...cur, name: cur.type != 'injected' ? cur.name : 'detail' }); return acc } , []);

    const [ entityType, setEntityType ] = useState(props.type);
    const [ queryString, setQueryString ] = useState(props.queryString);

    let queryStringFields = [ "label^5", "artist_label^3", "alt_label^5",
                   "note^3", "transcript^3",
                   "inscription", "accession" ];


    const [ subquery, setSubquery ] = useState(props.subquery ?? 'search')
    const [ filters, setFilters ] = useState(props.filters);
    const [ aggregations, setAggregations ] = useState({});
    const [ query, setQuery ] = useState(null);
    const [ count, setCount ] = useState(null);
    const [ fromIndex, setFromIndex ] = useState( props.fromIndex ?? 0);
    const [ pageSize, setPageSize ] = useState(50);
    const [ sortTerm, setSortTerm ] = useState( props.sortTerm ?? defaultSortConfig.field )
    const [ sortDir, setSortDir ] = useState( props.sortDir ?? defaultSortConfig.defaultDirection )
    const [ sortSetting, setSortSetting ] = useState( props.sortSetting ?? 'nameaz')
    const [ activeTab, setActiveTab ] = useState( props.activeTab ?? props.subquery ?? 'gok');

    const [ tabQueries, setTabQuery ] = useState( tabConfig?.filter( (tc) => tc.type === "subquery" ).reduce( (acc,tc) => {
      // * Creates the elasticsearch query for any tab of type "subquery" -- these counts are generated at first load
      //
      // This request uses the multi-search API, which serializes an index / routing header and the query object,
      // separated by a newline character
      //
      // See https://www.elastic.co/guide/en/elasticsearch/reference/7.17/search-multi-search.html

      // FIXME: This needs to consider filter terms
      var must = tc.must
      if (queryString && queryString !== '') {
        must = tc.must.concat({ query_string: { query: queryString, fields: queryStringFields, fuzziness: 2 }})
      }
      const query = {query: { bool: { must } }, size: 0 }

      if (filters && Object.keys(filters).length > 0) {
        const filterQuery = config.searchConfig.searchQuery(queryString, filters, entityType, pageSize, fromIndex, sortTerm, sortDir, subquery)
        query.post_filter = filterQuery.post_filter
      }

      acc += `{}\n${ JSON.stringify( query ) }\n` ;
      return acc
    }, '') );

    const [ resMode, setResMode ] = useState( config.searchConfig.viewMode ?? RESULTS_MODE.GRID);  // default mode

    const [ loaded, setLoaded ] = useState(false)
    const [ historyRoot, setHistoryRoot ] = useState(false);

    const results = useElasticSearch(endpoint, query);

    let tabCounts = useElasticSearch(endpoint.replace('_search','_msearch'),tabQueries)
    // console.log(receivedFilters)
    useEffect(() => {

      // Responds to any change in any of our search-facing params and sets the query, params to match
      // FIXME:
      let params = stateToParams({queryString, fromIndex, pageSize, filters, sortTerm, sortDir, sortSetting, subquery, activeTab})
      const url = new URL(window.location.href)
      url.search = params.toString()

      if ( !loaded ) {
        window.history.replaceState({params: params.toString(), isRoot: true},document.title,window.location.href)
        setLoaded(true)
      } else {

        if (url.search !== window.location.search) {
          window.history.pushState({params: params.toString(), isRoot: false},document.title,url)
        }

      }

      let mergedFilters = { ...filters, ...config.filters}
      let query = config.searchConfig.searchQuery(queryString, mergedFilters, entityType, pageSize, fromIndex, sortTerm, sortDir, subquery)

      setQuery(query)

    }, [queryString, fromIndex, pageSize, config.filters, filters, sortTerm, sortDir, sortSetting, subquery, activeTab]);

    const popStateHandler = (event) => {
      let state = event.state;
      var params = state?.params;

      if (params ) {

        for ( let [key,value] of Object.entries( paramsToState(params) ) ) {
          switch (key) {
            case 'filters':
              setFilters(value)
              continue;
            case 'fromIndex':
              setFromIndex(Number(value))
              continue;
            case 'pageSize':
              setPageSize(Number(value))
              continue;
            case 'queryString':
              setQueryString(value)
              continue;
            case 'sortTerm':
              setSortTerm(value)
              continue;
            case 'sortDir':
              setSortDir(value)
              continue;
            case 'sortSetting':
              setSortSetting(value)
              continue;
            case 'subquery':
              setSubquery(value)
              continue;
            case 'activeTab':
              setActiveTab(value)
              continue;
            default:
              continue
          }
        }


      }

    }

    // Startup hook
    // - Adds a popstate handler to the window for our callbacks on back-button presses
    useEffect(() => {

      window.onpopstate = (event) => { popStateHandler(event) }

    }, []);

    // Responds to the results from useElasticSearch
    useEffect(() => {

        if (results === null) { return; }
        let newCount = results?.hits?.total?.value ?? results?.hits?.total // NB: ES 6 vs ES 7 results formats
        setCount(newCount); // FIXME: Remove this after we move GOKM to ES 7
        setAggregations(results.aggregations);

    }, [results]);

    function changePage(newVal) {
        if ( newVal < count ) {
            setFromIndex(newVal);

            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            // WAS: $('.lower-page-start')?.offset()
            const lowerTop = document.querySelector('.lower-page-start')?.getBoundingClientRect().top + window.scrollY

            const topStuffHeight = 115;
            if (scrollTop >= lowerTop) {
              window.scrollTo(0, lowerTop + topStuffHeight);
            }
        }
    }

    function changeSort({sortDir,sortTerm}) {

      if (sortDir) {
        setSortDir(sortDir);
        setFromIndex(0);
      }

      if (sortTerm) {
        setSortTerm(sortTerm);
        setFromIndex(0);
      }

    }

    const applyFilters = (newFilters, unsets) => {
      let filt1 = {...filters, ...newFilters};
      for (const u of unsets) {
        delete filt1[u];
      }

      setFilters(filt1);
      setFromIndex(0);
    }

    const clearFilter = (filt) => {
      const clears = [filt]

      if (filt === 'height') {
        clears.push('height_reln')
      } else if (filt === 'width') {
        clears.push('width_reln')
      }

      if (filt === 'color') {
        clears.push('colorLabel')
      }
      applyFilters({}, clears);
    }

    const clearFilters = () => {
      setFilters({});
    }

    const clickForListMode = () => {
      setResMode(RESULTS_MODE.LIST);
    }

    const clickForGridMode = () => {
      setResMode(RESULTS_MODE.GRID);
    }

    const tabClicked = (tab) => {

      setActiveTab(tab)
      switch(tab) {
        case 'search':
          setSubquery('')
          break;
        case 'detail':
          break;
        default:
          setSubquery(tab)
      }
    }

    const [isActive, setActive] = useState(false);
    const toggleClass = () => {
      setActive(!isActive);
    };
    const barColors = [ "#D4C098", "#A9B5BA", "#5F859B", "#AF3B3D", "#B96C3E", "#825E85", "#5B5955", "#6F8261" ]

    return <div className="container-fluid">
      { labelConfig.resultsType === 'Exhibited Objects' ?
        <div className="rels-heading bg-bone-180">
          <YouSearched resultsForLabel={labelConfig.resultsFor} searchStr={props.queryString} typeLabel={labelConfig.resultsType} entityType={props.type} />
        </div>    :
        <section className="hero is-dark page-header-section">
        {
          props.captive===true || props.hideHeader===true ? '' : <div className="hero-body">
            <div className="columns">
              <div className="column is-4 results-heading">
                <YouSearched resultsForLabel={labelConfig.resultsFor} searchStr={props.queryString} typeLabel={labelConfig.resultsType} entityType={props.type} />
              </div>
              { labelConfig.resultsType ==='Art' ?
                <>
                <div className="column is-5">
                  {labelConfig.heroDescriptionText ?? ''}
                  </div>
                <div className='column is-3 data-call-out'>
                  <a href="/visualization"
                    > <img src="/assets/images/data-call-out.png" alt="data call out icon" role="presentation" aria-hidden="true"/>
                    <span>
                      <span className='small-text'>Visualization</span>
                      <span className="large-text">Georgia O'Keeffe's<br />Artistic Career</span>
                      <span className="icon icon-arrow-rt1 icon--white ml-2"></span>
                    </span>
                  </a>
                </div>
                </>
                :
                <div className="column is-8">
                  {labelConfig.heroDescriptionText ?? ''}
                </div>
              }
            </div>
          </div>
         }
          <div className="bg-dark">
            {
              searchConfig.showTabs ? <SearchTabBar countLabel={labelConfig.resultsCountTab} 
                                                    showTabs={searchConfig.showTabs} 
                                                    tabConfigs={tabConfig} 
                                                    clickCallback={tabClicked} 
                                                    activeTab={activeTab} 
                                                    count={count} 
                                                    tabCounts={tabCounts ?? {}} /> : ''
            }
          </div>
        </section>
      }
        <div className="section-body">
          <div className="hero object-section" id="section-archive-objects" style={{ display: activeTab !== 'detail' ? 'block' : 'none' }}>
            <BackNavButton label={props.backnavLabel} link={props.backnavLink} hidden={!(props.backnavLabel && props.backnavLink)} />
              {
                props.captive===true ?
                  <>
                    <div className="filtered-pill-box">
                      <div className="filtered-pill">
                        <span className="pr-1">Date:</span><span className="fw-bold">{`${config.filters?.startDate ?? 1917} - ${config.filters?.endDate ?? 1964}`}</span>
                      </div>
                      <div className={isActive ? 'dropdown is-active': 'dropdown'} >
                        <div className="dropdown-trigger">
                          <button
                            className="filtered-pill"
                            onClick={toggleClass}
                            aria-haspopup="true" aria-controls="dropdown-menu2">

                            <i className="icon icon-accordion-carets icon--slate-gray pr-1 mr-2"></i>
                            <span className="pr-1">Techniques:</span><span className="fw-bold">{`${8 - (config.filters?.notTechniques?.length ?? 0)} Selected`}</span>
                          </button>
                        </div>
                        <div class="dropdown-menu" id="dropdown-menu2" role="menu">
                          <div class="dropdown-content px-3 pb-2 counts-color-legend-swatches counts-color-legend-swatches-columns">
                            { props.captiveFilterControl }
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                    :
                  <FilterBar currentLabel={props.backnavCurrentLabel} customClasses={labelConfig.resultsType === 'Exhibited Objects' ? 'custom-bg':''} entityType={entityType} endpoint={endpoint} currentFilters={filters} applyFilters={applyFilters} clearFilter={clearFilter} clearFilters={clearFilters} aggregations={aggregations} count={count} fromIndex={fromIndex} pageSize={pageSize} changeSort={changeSort} sortTerm={sortTerm} sortDir={sortDir} />
              }

              <div className="hero-body">
                  <ResultsPageControl showViewMode={ config.searchConfig.viewMode === undefined ? true : false } labelConfig={labelConfig} changePage={changePage}  sortDir={sortDir} sortSetting={sortSetting} count={count} fromIndex={fromIndex} pageSize={pageSize} mode={resMode} resultsModeList={clickForListMode} resultsModeGrid={clickForGridMode} type={props.type} />
                  <ResultsView labels={labelConfig} results={results} count={count} mode={resMode} searchStr={props.queryString} />
                  <ResultsPageControl showViewMode={ config.searchConfig.viewMode === undefined ? true : false } labelConfig={labelConfig} changePage={changePage} changeSort={changeSort} sortTerm={sortTerm} sortDir={sortDir} sortSetting={sortSetting} count={count} fromIndex={fromIndex} pageSize={pageSize} mode={resMode} basiclayout={true} resultsModeList={clickForListMode} resultsModeGrid={clickForGridMode} type={props.type} />
              </div>
          </div>
          <div className="section section-zero-padding" id="section-archive-collections" style={{paddingTop:'12px', display: activeTab === 'detail' ? 'block' : 'none' }}>
            { props.injectedHTML ? parse(props.injectedHTML) : '' }
          </div>
        </div>
      </div>
}

window.addEventListener('DOMContentLoaded', (event) => {

  window.esQueries = esQueries
  let el = document.getElementById('appRoot');
  if (el) {

    let injectedHTML;

    if (el.innerHTML) {
      injectedHTML = DOMPurify.sanitize(el.innerHTML)
    }
    let dataset = document.getElementById('appRoot').dataset;

    // FIXME: if there is already a mapping global, use it below instead of ours?

    let dataMapping = window.dataMapping ?? dataMapping
    let searchConfig = window.searchConfig ?? searchPageSearchConfig

    let endpointPath = dataset.indexName ? `${dataset.indexName}/_search`: 'entity/_search'
    let endpointUrl = new URL(endpointPath, dataset.endpoint);

    let state = paramsToState(window.location.search)

    let staticFilters = {}

    if ( Object.keys(appRoot.dataset).includes("venues") ) {
      staticFilters.exhibition = JSON.parse(appRoot.dataset.venues)
    } else if ( Object.keys(appRoot.dataset).includes("archiveParent") ) {
      staticFilters.hierarchy = appRoot.dataset.archiveParent      
    }

    ReactDOM.createRoot(document.getElementById('appRoot')).render(
      <React.StrictMode>
        <SearchConfigProvider value={{endpoint: endpointUrl.href, mapping: dataMapping, searchConfig: searchConfig, filters: staticFilters }}>
          <SearchView queryString={''} filters={[]} {...{...appRoot.dataset, ...state, hideHeader: appRoot.dataset.hideHeader==="true" }} injectedHTML={injectedHTML} />
        </SearchConfigProvider>
      </React.StrictMode>  );

  }

})

export {SearchView};